import {json, objectToArray} from "p3";
import common from "../common";
import SceneLoader from "../ui/SceneLoader";

export default class Scene extends P3.Scene {
    /**
     * @param {string} sceneId
     */
    constructor(sceneId) {
        super();

        this._sceneId = sceneId;
        this._sceneLoaded = false;
        this._loader = null;
        this._tweens = [];
        this._manifest = [];
        this.signalListeners = [];
    }

    init() {
        super.init();

        let manifest = [];

        const loaderData = json("loaders")[this.sceneId];
        manifest = manifest.concat(objectToArray(loaderData.files));

        const sounds = objectToArray(loaderData.sounds);
        for (let i = 0, count = sounds.length; i < count; ++i) {
            const sound = sounds[i];
            sound.url = sound.url + P3.assets.audioFileExt;
        }
        manifest = manifest.concat(sounds);
        this._manifest = manifest;

        this._loader = new SceneLoader();

        this.loadScene();
    }

    destroy() {
        this._loader && this._loader.unload(false);

        for (let i in this.signalListeners) {
            this.signalListeners[i].detach();
        }

        super.destroy({children: true});
    }

    resize() {
        this.x = (P3.View.width - common.STAGE_WIDTH) * 0.5;
    }

    update() {
        if (this._loader) {
            if (this._loader.parent) {
                this._loader.update();
                this._loader.parent.addChild(this._loader);
            }
        }
        this.cleanupTweens();
    }

    loadScene(title) {
        this.addSceneLoader(this.manifest, title).then(() => this.onSceneLoaded());
    }

    onSceneLoaded() {
        this._sceneLoaded = true;
    }

    /**
     * @param {object} manifest
     * @returns {Promise}
     */
    addSceneLoader(manifest, title) {

        this._loader.setTitle(title);

        return new Promise((resolve, reject) => {
            const count = Object.keys(manifest).length;
            if (count > 0) {
                this.addChild(this._loader);
                this._loader.animateIn().then(() => this._loader.load(manifest, resolve));
            } else {
                resolve();
            }
        });
    }

    /**
     * @param {number} seconds
     * @returns {Promise}
     */
    delay(seconds) {
        return new Promise((resolve, reject) => {
            this.addTween(P3.utils.delay(seconds, resolve));
        });
    }

    /**
     * @param {gsap.core.Tween|gsap.core.Timeline} tween
     */
    addTween(tween) {
        this.removeTween(tween);
        this._tweens.push(tween);
        return tween;
    }

    /**
     * @param {gsap.core.Tween|gsap.core.Timeline} tween
     * @param {boolean} [kill=true]
     */
    removeTween(tween, kill = true) {
        for (let i = 0; i < this._tweens.length; ++i) {
            if (this._tweens[i] === tween) {
                kill && this._tweens[i].kill();
                this._tweens.splice(i, 1);
            }
        }
    }

    /**
     * @param {boolean} [kill=false]
     */
    removeAllTweens(kill = false) {
        if (kill) {
            for (let i = 0, count = this._tweens.length; i < count; ++i) {
                this._tweens[i].kill();
            }
        }
        this._tweens.length = 0;
    }

    cleanupTweens() {
        for (let i = this._tweens.length - 1; i >= 0; --i) {
            const tween = this._tweens[i];
            if (tween.progress() >= 1 && !tween.isActive()) {
                tween.kill(true);
                this._tweens.splice(i, 1);
            }
        }
    }

    pauseTweens() {
        for (let i = 0, count = this._tweens.length; i < count; ++i) {
            this._tweens[i].pause();
        }
    }

    resumeTweens() {
        for (let i = 0, count = this._tweens.length; i < count; ++i) {
            this._tweens[i].resume();
        }
    }

    /**
     * @returns {string}
     */
    get sceneId() {
        return this._sceneId;
    }

    /**
     * @returns {[]}
     */
    get manifest() {
        return this._manifest.slice(0);
    }

    /**
     * @returns {boolean}
     */
    get isSceneLoaded() {
        return this._sceneLoaded;
    }
}